import { useCallback, useEffect, useRef } from 'react';
import { api } from '../lib/api';

/**
 * De quanto em quanto tempo o painel repete o aviso enquanto o atendente digita.
 *
 * O servidor dá um prazo de dez segundos a cada sinal; repetir a cada quatro
 * mantém o aviso aceso com folga para uma requisição perdida no caminho, sem
 * transformar cada tecla em uma chamada à API.
 */
const RENEW_MS = 4_000;

/**
 * Silêncio que conta como "parou de digitar".
 *
 * Mais curto que o prazo do servidor de propósito: quem para de escrever no meio
 * de uma frase — para ler o histórico, para pensar — não deve continuar
 * aparecendo como digitando na conversa do cliente.
 */
const IDLE_MS = 3_000;

export interface TypingSignal {
  /** O atendente digitou algo agora. */
  keystroke: () => void;
  /** Parou de escrever (mensagem enviada, campo limpo, conversa fechada). */
  stop: () => void;
}

/**
 * Reproduz no WhatsApp do cliente o "digitando..." de quem atende.
 *
 * O cliente não vê o painel: sem isto, o intervalo entre a pergunta dele e a
 * resposta é silêncio absoluto, e é nesse silêncio que ele manda "alô?" três
 * vezes ou desiste. O bot já ganha o mesmo aviso enquanto pensa; este é o do
 * atendente humano.
 *
 * O aviso é mantido por renovação, não por um par liga/desliga: assim uma aba
 * fechada no meio da frase, ou a rede caindo, apaga o aviso pelo prazo do
 * servidor em vez de deixar o cliente esperando uma mensagem que não vem.
 */
export function useTypingSignal(conversationId: string | null, enabled: boolean): TypingSignal {
  const sentAt = useRef(0);
  const idleTimer = useRef<number | null>(null);
  /**
   * Conversa para a qual o último aviso saiu.
   *
   * Guardada porque o `stop` precisa alcançar a conversa que estava aberta, e não
   * a que acabou de ser aberta: trocar de atendimento com o aviso aceso deixaria
   * o cliente anterior vendo digitação que ninguém está fazendo.
   */
  const active = useRef<string | null>(null);

  const clearIdle = () => {
    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    idleTimer.current = null;
  };

  const stop = useCallback(() => {
    clearIdle();
    const id = active.current;
    if (!id) return;

    active.current = null;
    sentAt.current = 0;
    // Falha aqui não vira erro na tela: o aviso expira sozinho no servidor, e
    // "não consegui avisar que você parou de digitar" não é notícia para quem
    // está atendendo.
    void api.setTyping(id, false).catch(() => undefined);
  }, []);

  const keystroke = useCallback(() => {
    if (!conversationId || !enabled) return;

    // Trocou de conversa com o aviso aceso: apaga o da anterior antes.
    if (active.current && active.current !== conversationId) stop();

    clearIdle();
    idleTimer.current = window.setTimeout(stop, IDLE_MS);

    const now = Date.now();
    if (now - sentAt.current < RENEW_MS) return;

    sentAt.current = now;
    active.current = conversationId;
    void api.setTyping(conversationId, true).catch(() => undefined);
  }, [conversationId, enabled, stop]);

  // Sair da conversa, ou do painel, apaga o aviso: sem isto o cliente ficaria
  // com um "digitando..." de alguém que já foi embora, até o prazo do servidor.
  useEffect(() => stop, [conversationId, stop]);

  return { keystroke, stop };
}
